import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Drawer, Input, Alert, Button } from 'antd';
import { ClearOutlined } from '@ant-design/icons';
import { api } from '../api';
import { t } from '../i18n';

// Per-browser dismissal of the "commands run on the device" hint (once closed it
// stays closed for this user), and the console-clear watermark is per device.
const HINT_KEY = 'ddisk.deviceConsoleHintDismissed';

/** Per-device console (superadmin): a command RELAY over the device's heartbeat.
 * Not a live PTY — you enter a command, it runs on the device on its next beat,
 * and the output returns a few seconds later. Shown as a scrolling command log. */
export function DeviceConsole({
  deviceId,
  deviceName,
  open,
  onClose,
}: {
  deviceId: string;
  deviceName: string;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  // Clear = hide everything up to the current newest command (local, per device);
  // new commands keep appearing. Persisted per device so it survives reopen.
  const clearKey = `ddisk.deviceConsoleCleared.${deviceId}`;
  const [clearedBeforeId, setClearedBeforeId] = useState<number>(() => Number(localStorage.getItem(clearKey) ?? 0));
  const [hintDismissed, setHintDismissed] = useState<boolean>(() => localStorage.getItem(HINT_KEY) === '1');
  const q = useQuery({
    queryKey: ['device-console', deviceId],
    queryFn: () => api.deviceConsoleCommands(deviceId),
    enabled: open,
    refetchInterval: open ? 2000 : false,
  });
  const exec = useMutation({
    mutationFn: (command: string) => api.deviceConsoleExec(deviceId, command),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device-console', deviceId] }),
  });

  const bottom = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [q.data]);

  const submit = (): void => {
    const c = draft.trim();
    if (!c) return;
    setDraft('');
    exec.mutate(c);
  };

  const allCmds = q.data?.commands ?? [];
  const cmds = allCmds.filter((c) => c.id > clearedBeforeId);
  const clearConsole = (): void => {
    const maxId = allCmds.reduce((m, c) => Math.max(m, c.id), clearedBeforeId);
    setClearedBeforeId(maxId);
    localStorage.setItem(clearKey, String(maxId));
  };
  const dismissHint = (): void => {
    localStorage.setItem(HINT_KEY, '1');
    setHintDismissed(true);
  };

  return (
    <Drawer
      title={t('deviceConsoleTitle', { name: deviceName })}
      placement="right"
      width={720}
      open={open}
      onClose={onClose}
      extra={
        <Button size="small" icon={<ClearOutlined />} onClick={clearConsole} disabled={cmds.length === 0}>
          {t('deviceConsoleClear')}
        </Button>
      }
      styles={{ body: { display: 'flex', flexDirection: 'column', gap: 10, background: '#0d1220' } }}
    >
      {!hintDismissed && (
        <Alert
          type="info"
          showIcon
          closable
          onClose={dismissHint}
          message={t('deviceConsoleHint')}
          style={{ flex: '0 0 auto' }}
        />
      )}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          fontFamily: 'Menlo, Consolas, monospace',
          fontSize: 13,
          color: '#e8ecf4',
          background: '#0d1220',
          padding: 8,
          borderRadius: 6,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {/* Explicit terminal colour — the console pane is always dark, so a
            theme-following secondary colour would vanish in light mode. */}
        {cmds.length === 0 && <span style={{ color: '#8b95ad' }}>{t('deviceConsoleEmpty')}</span>}
        {cmds.map((c) => (
          <div key={c.id} style={{ marginBottom: 10 }}>
            <div>
              <span style={{ color: '#5b8def' }}>$ </span>
              <span style={{ color: '#e8ecf4' }}>{c.command}</span>
            </div>
            {c.status === 'done' ? (
              <div style={{ color: (c.exitCode ?? 0) === 0 ? '#c7d2e6' : '#f87171' }}>
                {c.output || ''}
                {c.exitCode ? `\n[exit ${c.exitCode}]` : ''}
              </div>
            ) : (
              <div style={{ color: '#8b95ad' }}>{t('deviceConsoleRunning')}</div>
            )}
          </div>
        ))}
        <div ref={bottom} />
      </div>
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={t('deviceConsolePlaceholder')}
        onPressEnter={submit}
        autoFocus
        style={{ fontFamily: 'Menlo, Consolas, monospace', flex: '0 0 auto' }}
        addonBefore={<span style={{ color: '#5b8def' }}>$</span>}
      />
    </Drawer>
  );
}
